/**
 * MultiSelectField — desplegable de selección múltiple con «Seleccionar todos».
 *
 * El botón se ve como un `<select>` y resume lo elegido («Factura, Boleta y 3
 * más» · «Todos (12)»); al abrirlo, casillas agrupadas. Para listas cortas o
 * medianas (hasta unas decenas de opciones): no tiene buscador.
 *
 * ⚠️ La capa va en `z-[100]`, sobre el modal del design system (z-index 80):
 * se puede abrir desde un modal (lo vigila capasFlotantes.test.ts).
 */
import { useId, useMemo, type ReactNode } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { ChevronDown } from 'lucide-react'

export interface MultiSelectOption<T extends string | number = string> {
  value: T
  label: string
  /** Agrupa las opciones bajo un subtítulo, en el orden en que aparece cada grupo. */
  group?: string
  hint?: string
}

export interface MultiSelectFieldProps<T extends string | number = string> {
  label?: ReactNode
  options: MultiSelectOption<T>[]
  value: T[]
  onChange: (value: T[]) => void
  placeholder?: string
  /** Rótulo del resumen cuando están todas: «Todos los tipos». */
  allLabel?: string
  hint?: ReactNode
  error?: ReactNode
  disabled?: boolean
  dense?: boolean
  id?: string
  className?: string
}

function resumen<T extends string | number>(opciones: MultiSelectOption<T>[], elegidas: T[], todos: string, vacio: string) {
  if (!elegidas.length) return vacio
  if (elegidas.length === opciones.length) return `${todos} (${opciones.length})`
  const nombres = opciones.filter(o => elegidas.includes(o.value)).map(o => o.label)
  if (nombres.length <= 2) return nombres.join(', ')
  return `${nombres.slice(0, 2).join(', ')} y ${nombres.length - 2} más`
}

export function MultiSelectField<T extends string | number = string>({
  label, options, value, onChange, placeholder = 'Elegir…', allLabel = 'Todos', hint, error,
  disabled = false, dense = false, id: idProp, className = '',
}: MultiSelectFieldProps<T>) {
  const autoId = useId()
  const id = idProp ?? `multi-${autoId}`
  const todas = value.length === options.length && options.length > 0
  const algunas = value.length > 0 && !todas

  const grupos = useMemo(() => {
    const salida: { nombre: string; opciones: MultiSelectOption<T>[] }[] = []
    for (const o of options) {
      const nombre = o.group ?? ''
      const grupo = salida.find(g => g.nombre === nombre) ?? salida[salida.push({ nombre, opciones: [] }) - 1]
      grupo.opciones.push(o)
    }
    return salida
  }, [options])

  // Se conserva el orden de las opciones, no el de los clics.
  const alternar = (v: T) => {
    const nuevas = value.includes(v) ? value.filter(x => x !== v) : [...value, v]
    onChange(options.map(o => o.value).filter(x => nuevas.includes(x)))
  }

  return (
    <div className={`cl-field ${dense ? 'cl-field--dense' : ''} ${className}`.trim()}>
      {label && <label className="cl-label" htmlFor={id}>{label}</label>}
      <Popover.Root>
        <Popover.Trigger asChild disabled={disabled}>
          <button id={id} type="button"
            className={`${dense ? 'cl-input cl-input--sm' : 'cl-input'} flex items-center justify-between gap-2 text-left`}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}>
            <span className={`truncate ${value.length ? '' : 'text-content-subtle'}`}>
              {resumen(options, value, allLabel, placeholder)}
            </span>
            <ChevronDown size={14} className="shrink-0 text-content-subtle" aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="start" sideOffset={4}
            className="z-[100] bg-popover rounded-xl shadow-lg border border-divider p-2 max-h-80 overflow-y-auto"
            style={{ backgroundColor: 'rgb(var(--color-popover))', minWidth: 'var(--radix-popover-trigger-width)' }}>
            <fieldset className="space-y-1">
              <legend className="sr-only">{typeof label === 'string' ? label : 'Opciones'}</legend>
              <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm font-medium text-content hover:bg-soft cursor-pointer">
                <input type="checkbox" checked={todas}
                  ref={el => { if (el) el.indeterminate = algunas }}
                  onChange={() => onChange(todas ? [] : options.map(o => o.value))} />
                Seleccionar todos
              </label>
              <div className="border-t border-divider my-1" />
              {grupos.map(g => (
                <div key={g.nombre} className="space-y-0.5">
                  {g.nombre && (
                    <p className="px-2 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-subtle">{g.nombre}</p>
                  )}
                  {g.opciones.map(o => (
                    <label key={String(o.value)} title={o.hint}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-content hover:bg-soft cursor-pointer">
                      <input type="checkbox" checked={value.includes(o.value)} onChange={() => alternar(o.value)} />
                      {o.label}
                    </label>
                  ))}
                </div>
              ))}
            </fieldset>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {error && <div className="cl-error" id={`${id}-error`} role="alert">{error}</div>}
      {hint && !error && <div className="cl-hint" id={`${id}-hint`}>{hint}</div>}
    </div>
  )
}
